/* ============================================================================
   Velocity — Abrasives Index collection page behaviour
   AJAX toolbar (tabs · facets · sort · pagination) · sortable headers ·
   bulk bench · quick-add · cart drawer. Vanilla JS, scoped to .afx-coll.

   Architecture: ALL interaction is event-delegated from the persistent root
   (.afx-coll). Navigation (tabs/facets/sort/pager) swaps only [data-afx-main]
   in place via fetch + DOMParser (Section-Rendering style), pushState keeps the
   real URL (SEO + deep-link + back/forward), and falls back to a full page nav
   if anything goes wrong. Because every listener lives on root, swapping the
   inner DOM never orphans a handler — no re-binding, no double-binding.

   Built for INP: delegation (no per-row listeners), rAF-batched bench writes.
   Theme-editor safe: boots on load + shopify:section:load, tears down on unload.
   ============================================================================ */
(function () {
  'use strict';

  var CFG = window.AFX_COLL || { money_format: '${{amount}}', freeShip: 99, tiers: [], cartUrl: '/cart' };

  /* ── pure helpers ───────────────────────────────────────────────────────── */
  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function formatMoney(cents) {
    var value = (cents / 100).toFixed(2).split('.');
    var dollars = value[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (CFG.money_format || '${{amount}}')
      .replace(/\{\{\s*amount\s*\}\}/, dollars + '.' + value[1])
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, dollars);
  }
  function param() { return new URLSearchParams(location.search); }
  function tierFor(sub) {
    var tiers = (CFG.tiers || []).slice().sort(function (a, b) { return a.at - b.at; });
    var pct = 0, at = null, nextPct = 0;
    for (var i = 0; i < tiers.length; i++) {
      if (sub >= tiers[i].at) pct = tiers[i].pct;
      else { at = tiers[i].at; nextPct = tiers[i].pct; break; }
    }
    return { pct: pct, nextAt: at, nextPct: nextPct };
  }

  /* ── notify the Empire header + flyout of the fresh cart (mirror v2.js) ───── */
  function broadcastCart(cart) {
    if (!cart || typeof cart.item_count !== 'number') return;
    try { window.dispatchEvent(new CustomEvent('cartcount:update', { detail: cart })); } catch (e) {}
    try { document.dispatchEvent(new CustomEvent('product-form:cart:update', { bubbles: true, detail: { cart: cart } })); } catch (e) {}
  }
  function fetchCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) { return (d && typeof d.item_count === 'number') ? d : null; })
      .catch(function () { return null; });
  }

  /* ── module state (one .afx-coll per collection page) ─────────────────────── */
  var root = null, BENCH_KEY = 'afx:bench', bench = {};
  var ro = null, siteHeader = null, navToken = 0, inFlight = false, qtyBusy = false, drawerFetching = false;
  var toastEl = null, toastT = null, lastFocus = null;

  function $(s, c) { return (c || root).querySelector(s); }
  function $all(s, c) { return Array.prototype.slice.call((c || root).querySelectorAll(s)); }

  /* ====================================================================
     AJAX NAVIGATION — swap [data-afx-main] in place, keep the real URL.
     Preserves rendering context (preview_theme_id + view) so it works on
     a preview theme and on a subcollection that renders via ?view=abrasives.
     ==================================================================== */
  function withContext(url) {
    try {
      var u = new URL(url, location.origin);
      var cur = new URLSearchParams(location.search);
      ['preview_theme_id', 'view'].forEach(function (k) {
        if (cur.has(k) && !u.searchParams.has(k)) u.searchParams.set(k, cur.get(k));
      });
      return u.pathname + u.search + u.hash;
    } catch (e) { return url; }
  }
  function headerOffset() {
    var v = root && getComputedStyle(root).getPropertyValue('--afx-header-h');
    return (parseInt(v, 10) || 0);
  }
  function afxNavigate(url, push) {
    var target = withContext(url);
    var main = $('[data-afx-main]');
    if (!main) { location.href = target; return; }
    var token = ++navToken;
    main.setAttribute('aria-busy', 'true');
    main.classList.add('is-loading');
    fetch(target, { headers: { 'X-Requested-With': 'fetch' }, credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('http ' + r.status); return r.text(); })
      .then(function (html) {
        if (token !== navToken) return;                       // a newer nav won
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-afx-main]');
        if (!fresh) throw new Error('no [data-afx-main] in response');
        main.replaceWith(fresh);                              // swap only the results region
        if (push !== false) { try { history.pushState({ afx: 1 }, '', target); } catch (e) {} }
        initView();                                          // re-init the swapped DOM
        // a11y: move keyboard focus into the refreshed results + announce the new count
        var freshMain = $('[data-afx-main]');
        if (freshMain) { try { freshMain.focus({ preventScroll: true }); } catch (e) {} }
        announceView();
        var y = root.getBoundingClientRect().top + window.pageYOffset - headerOffset() - 8;
        try { window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' }); } catch (e) { window.scrollTo(0, Math.max(0, y)); }
      })
      .catch(function () { location.href = target; });        // graceful fallback: full nav
  }

  function submitFacetsAjax(changed) {
    var form = $('[data-facet-form]');
    if (!form) return;
    var params = new URLSearchParams();
    new FormData(form).forEach(function (v, k) { if (v !== '' && v != null) params.append(k, v); });
    // keep an active client grit/rating sort UNLESS the user just picked a native sort
    // from the dropdown (in which case the dropdown must win).
    var isSortChange = changed && changed.matches && changed.matches('[data-sort-select]');
    if (!isSortChange) { var cur = param(); if (cur.get('afx_sort')) params.set('afx_sort', cur.get('afx_sort')); }
    afxNavigate(location.pathname + '?' + params.toString(), true);
  }

  function onSortClick(th) {
    var asc = th.getAttribute('data-sort-asc'), desc = th.getAttribute('data-sort-desc');
    var native = th.hasAttribute('data-sort-native');
    var ascKey = native ? asc : asc.replace('afx:', '');
    var descKey = desc ? (native ? desc : desc.replace('afx:', '')) : null;
    var cur = param();
    var curVal = native ? (cur.get('sort_by') || '') : (cur.get('afx_sort') || '');
    var goKey = (curVal === ascKey && descKey) ? descKey : ascKey;
    var next = param(); next.delete('page');
    if (native) { next.set('sort_by', goKey); next.delete('afx_sort'); }
    else { next.set('afx_sort', goKey); if (!next.get('sort_by')) next.set('sort_by', 'manual'); }
    afxNavigate(location.pathname + '?' + next.toString(), true);
  }

  /* ====================================================================
     PER-VIEW INIT — runs on boot AND after every AJAX swap.
     ==================================================================== */
  function observeSticky() {
    if (!window.ResizeObserver) return;
    if (!ro) {
      ro = new ResizeObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var el = entries[i].target;
          var h = Math.round(el.getBoundingClientRect().height);
          if (el === siteHeader) root.style.setProperty('--afx-header-h', h + 'px');
          else root.style.setProperty('--afx-toolbar-h', h + 'px');
        }
      });
    }
    ro.disconnect();
    siteHeader = document.querySelector('header, .header, [data-header], #shopify-section-header, .site-header');
    if (siteHeader) ro.observe(siteHeader);
    var tb = $('.afx-toolbar'); if (tb) ro.observe(tb);
  }

  function applySortState() {
    var p = param(), sortBy = p.get('sort_by') || '', afxSort = p.get('afx_sort') || '';
    $all('.afx-th[data-sort-asc]').forEach(function (th) {
      var asc = th.getAttribute('data-sort-asc'), desc = th.getAttribute('data-sort-desc');
      var native = th.hasAttribute('data-sort-native');
      var ascKey = native ? asc : asc.replace('afx:', '');
      var descKey = desc ? (native ? desc : desc.replace('afx:', '')) : null;
      var cur = native ? sortBy : afxSort;
      // an active grit/rating (afx) sort sets sort_by=manual; don't let that light the '#' column
      if (native && afxSort) cur = '';
      th.classList.remove('is-sort', 'is-asc', 'is-desc');
      if (cur === ascKey) { th.classList.add('is-sort', 'is-asc'); th.setAttribute('aria-sort', 'ascending'); }
      else if (descKey && cur === descKey) { th.classList.add('is-sort', 'is-desc'); th.setAttribute('aria-sort', 'descending'); }
      else { th.setAttribute('aria-sort', 'none'); }
    });
  }

  // client-side reorder for grit / rating on the loaded page (intentional: page-only)
  function clientSort() {
    var afxSort = param().get('afx_sort') || '';
    if (!afxSort) return;
    var tb = $('[data-bench-rows]');
    if (!tb) return;
    var key = afxSort.indexOf('grit') === 0 ? 'gritNum' : (afxSort.indexOf('rating') === 0 ? 'rating' : null);
    if (!key) return;
    var dir = afxSort.slice(-4) === 'desc' ? -1 : 1;
    var rows = $all('.afx-row', tb);
    rows.sort(function (a, b) {
      var av = parseFloat(a.dataset[key]) || 0, bv = parseFloat(b.dataset[key]) || 0;
      if (!av) av = dir > 0 ? Infinity : -Infinity;
      if (!bv) bv = dir > 0 ? Infinity : -Infinity;
      return (av - bv) * dir;
    });
    var frag = document.createDocumentFragment();
    rows.forEach(function (r) { frag.appendChild(r); });
    tb.appendChild(frag);
  }

  function initView() {
    observeSticky();
    applySortState();
    clientSort();
    syncChecks();
    renderBench();
  }

  function announceView() {
    var status = root.querySelector('[data-afx-status]');
    if (!status) return;
    var n = $('.afx-count-n'), h = $('.afx-h1');
    status.textContent = (h ? h.textContent.trim() + ': ' : '') + (n ? n.textContent.trim() : '') + ' products';
  }

  /* ====================================================================
     BULK BENCH — localStorage-backed, rAF-batched render.
     ==================================================================== */
  function loadBench() { try { return JSON.parse(localStorage.getItem(BENCH_KEY)) || {}; } catch (e) { return {}; } }
  function saveBench() { try { localStorage.setItem(BENCH_KEY, JSON.stringify(bench)); } catch (e) {} }

  var rafPending = false;
  function scheduleRender() {
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame(function () { rafPending = false; renderBench(); syncChecks(); });
  }
  function renderBench() {
    var bar = $('[data-bench-bar]');
    if (!bar) return;
    var ids = Object.keys(bench);
    if (!ids.length) { bar.hidden = true; return; }
    bar.hidden = false;
    var subC = ids.reduce(function (s, id) { return s + (bench[id].price || 0); }, 0);
    var sub = subC / 100, t = tierFor(sub);
    setText('[data-bench-n]', ids.length);
    setText('[data-bench-noun]', ids.length === 1 ? 'item' : 'items');
    setText('[data-bench-sub]', formatMoney(subC) + ' selected');
    var msg, prog;
    if (t.nextAt) {
      prog = Math.min(100, sub / t.nextAt * 100);
      var freeRem = Math.max(0, (CFG.freeShip || 0) - sub);
      msg = (t.pct ? 'You’ve unlocked ' + t.pct + '% off. ' : '') +
        'Add ' + formatMoney((t.nextAt - sub) * 100) + ' more to hit ' + t.nextPct + '% off' +
        (freeRem > 0 ? ' · ' + formatMoney(freeRem * 100) + ' to free shipping' : '') + '.';
    } else if (t.pct > 0) {
      prog = 100; msg = 'Max volume pricing unlocked — ' + t.pct + '% off + free shipping. Nice haul.';
    } else {
      prog = 100;
      var freeRem0 = Math.max(0, (CFG.freeShip || 0) - sub);
      msg = freeRem0 > 0 ? 'Add ' + formatMoney(freeRem0 * 100) + ' more for free shipping.' : 'Ships today.';
    }
    setText('[data-bench-msg]', msg);
    var prg = $('[data-bench-prog]');
    if (prg) { prg.style.width = prog + '%'; prg.setAttribute('aria-valuenow', String(Math.round(prog))); }
  }
  function setText(sel, val) { var el = $(sel); if (el) el.textContent = val; }

  function syncChecks() {
    var tb = $('[data-bench-rows]');
    var all = $all('.afx-bench-cb', tb || root);
    all.forEach(function (cb) {
      var on = !!bench[cb.getAttribute('data-variant-id')];
      if (cb.checked !== on) cb.checked = on;
      var row = cb.closest('.afx-row');
      if (row) row.classList.toggle('is-sel', on);
    });
    var head = $('[data-bench-all]');
    if (head) {
      var allOn = all.length > 0 && all.every(function (cb) { return cb.checked; });
      head.classList.toggle('is-on', allOn);
      head.setAttribute('aria-pressed', String(allOn));
    }
  }
  function setSel(cb, on) {
    var id = cb.getAttribute('data-variant-id');
    if (on) bench[id] = { price: parseInt(cb.getAttribute('data-price'), 10) || 0, title: cb.getAttribute('data-title'), available: cb.getAttribute('data-available') !== '0' };
    else delete bench[id];
  }
  function toggleAll() {
    var tb = $('[data-bench-rows]');
    var cbs = $all('.afx-bench-cb', tb || root);
    var target = !cbs.every(function (c) { return c.checked; });
    cbs.forEach(function (cb) { cb.checked = target; setSel(cb, target); });
    saveBench(); scheduleRender();
  }
  function benchAddToCart() {
    // drop any unavailable id so one sold-out line can't 422 the all-or-nothing batch.
    var ids = Object.keys(bench).filter(function (id) { return bench[id].available !== false; });
    if (!ids.length) return;
    cartAdd(ids.map(function (id) { return { id: id, quantity: 1 }; }),
      ids.length + ' items added — volume pricing applies at checkout',
      function () { bench = {}; saveBench(); scheduleRender(); openDrawer(); }, $('[data-bench-add]'));
  }

  /* ====================================================================
     CART (AJAX API) + minimal drawer with focus trap.
     ==================================================================== */
  function cartAdd(items, msg, done, btn) {
    if (inFlight) return;
    inFlight = true;
    if (btn) btn.disabled = true;
    fetch('/cart/add.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      return r.json().then(function (d) { return { ok: r.ok, status: r.status, d: d }; });
    }).then(function (res) {
      if (!res.ok) { toast((res.d && res.d.description) || 'Could not add — check stock.'); return; }
      toast(msg);
      fetchCart().then(function (cart) {
        if (!cart) return fetchCart().then(function (c2) { broadcastCart(c2); return c2; });
        broadcastCart(cart); return cart;
      });
      if (CFG.useThemeDrawer) document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      else refreshDrawer();
      if (done) done();
    }).catch(function () {
      toast('That didn’t take. Try again, or text us.');
    }).then(function () {
      inFlight = false; if (btn) btn.disabled = false;
    });
  }

  function focusables(drawer) {
    return $all('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])', drawer);
  }
  function onDrawerKeydown(e) {
    var drawer = $('[data-drawer]');
    if (!drawer || drawer.hidden) return;
    if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); closeDrawer(); return; }
    if (e.key !== 'Tab') return;
    var f = focusables(drawer); if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }
  function openDrawer() {
    var drawer = $('[data-drawer]'), scrim = $('[data-drawer-scrim]');
    if (CFG.useThemeDrawer || !drawer) { location.href = CFG.cartUrl; return; }
    lastFocus = document.activeElement;
    drawer.hidden = false;
    if (scrim) scrim.hidden = false;
    var main = $('[data-afx-main]'); if (main) try { main.inert = true; } catch (e) {}   // background truly inert for AT
    refreshDrawer();
    var close = $('[data-drawer-close]'); if (close) close.focus();
  }
  function closeDrawer() {
    var drawer = $('[data-drawer]'), scrim = $('[data-drawer-scrim]');
    if (drawer) drawer.hidden = true;
    if (scrim) scrim.hidden = true;
    var main = $('[data-afx-main]'); if (main) try { main.inert = false; } catch (e) {}
    if (lastFocus && lastFocus.focus) { try { lastFocus.focus(); } catch (e) {} }
    lastFocus = null;
  }
  function refreshDrawer() {
    var drawer = $('[data-drawer]');
    if (!drawer || drawerFetching) return;          // dedupe concurrent /cart.js reads
    drawerFetching = true;
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) { if (cart && typeof cart.item_count === 'number') { renderDrawer(cart); broadcastCart(cart); } })
      .catch(function () {})
      .then(function () { drawerFetching = false; });
  }
  function renderDrawer(cart) {
    var body = $('[data-drawer-body]'), foot = $('[data-drawer-foot]');
    if (!body) return;
    if (!cart.item_count) {
      body.innerHTML = '<div style="text-align:center;padding:60px 10px;color:var(--muted)"><div style="font-family:var(--font-display);font-weight:700;font-size:18px;color:var(--ink)">Nothing here yet.</div><div style="margin-top:8px;font-size:14px">Tick rows below to bulk-add.</div></div>';
      if (foot) foot.innerHTML = '';
      return;
    }
    body.innerHTML = cart.items.map(function (it) {
      var name = escapeHtml(it.product_title), vendor = escapeHtml(it.vendor || '');
      return '<div class="afx-dl"><div class="afx-dl-sw" style="background:linear-gradient(135deg,#cfd8e0,#9fb0c0)"></div>' +
        '<div style="flex:1;min-width:0"><div class="afx-dl-meta">' + vendor + '</div>' +
        '<div class="afx-dl-name">' + name + '</div><div class="afx-dl-row"><div class="afx-qty">' +
        '<button data-dq="-" data-key="' + it.key + '" aria-label="Decrease quantity of ' + name + '">−</button>' +
        '<span>' + it.quantity + '</span>' +
        '<button data-dq="+" data-key="' + it.key + '" aria-label="Increase quantity of ' + name + '">+</button>' +
        '</div><strong>' + formatMoney(it.final_line_price) + '</strong></div></div></div>';
    }).join('');
    var t = tierFor(cart.total_price / 100);
    var disc = t.pct ? cart.total_price * t.pct / 100 : 0;
    if (foot) foot.innerHTML =
      '<div style="display:flex;justify-content:space-between;font-size:13px;color:var(--muted);margin-bottom:6px"><span>Subtotal</span><span>' + formatMoney(cart.total_price) + '</span></div>' +
      (disc ? '<div style="display:flex;justify-content:space-between;font-size:13px;color:var(--accent);font-weight:600;margin-bottom:6px"><span>Volume −' + t.pct + '%</span><span>−' + formatMoney(disc) + '</span></div>' : '') +
      '<div style="display:flex;align-items:baseline;justify-content:space-between;margin:10px 0 14px"><span style="font-family:var(--font-mono);font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:var(--muted)">Total</span><span style="font-family:var(--font-display);font-weight:800;font-size:26px;color:var(--accent)">' + formatMoney(cart.total_price - disc) + '</span></div>' +
      '<a class="afx-checkout" href="/checkout">Checkout — ships today</a>';
  }
  function onQtyClick(b) {
    if (qtyBusy) return;
    var key = b.getAttribute('data-key');
    var delta = b.getAttribute('data-dq') === '+' ? 1 : -1;
    qtyBusy = true;
    var foot = $('[data-drawer-foot]');
    var qtyBtns = foot ? $all('[data-dq]', foot) : [];
    qtyBtns.forEach(function (x) { x.disabled = true; });
    // read the freshest line qty from /cart.js, then change relative to it
    fetchCart().then(function (cart) {
      var line = cart && cart.items.filter(function (it) { return it.key === key; })[0];
      var q = (line ? line.quantity : 1) + delta;
      return fetch('/cart/change.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: Math.max(0, q) })
      }).then(function (r) { return r.json().then(function (d) { return { ok: r.ok, cart: d }; }); });
    }).then(function (res) {
      qtyBusy = false;
      if (!res.ok || typeof res.cart.item_count !== 'number') {
        // 422 (over-stock / line limit): don't render the error object as an empty cart.
        toast((res.cart && res.cart.description) || 'Could not update quantity.');
        refreshDrawer();                              // restore from the authoritative cart
        return;
      }
      renderDrawer(res.cart); broadcastCart(res.cart);
    }).catch(function () {
      qtyBusy = false; qtyBtns.forEach(function (x) { x.disabled = false; });
    });
  }

  function toast(msg) {
    if (!toastEl) toastEl = $('[data-toast]');
    if (!toastEl) return;
    toastEl.textContent = msg; toastEl.hidden = false;
    clearTimeout(toastT);
    toastT = setTimeout(function () { toastEl.hidden = true; }, 2200);
  }

  /* ====================================================================
     DELEGATED LISTENERS — bound ONCE on the persistent root.
     ==================================================================== */
  function onRootClick(e) {
    var t = e.target;
    if (t.closest('[data-drawer-close]')) { closeDrawer(); return; }
    if (t.matches && t.matches('[data-drawer-scrim]')) { closeDrawer(); return; }
    var dq = t.closest('[data-dq]'); if (dq) { onQtyClick(dq); return; }
    var add = t.closest('[data-quick-add]'); if (add) { cartAdd([{ id: add.getAttribute('data-variant-id'), quantity: 1 }], add.getAttribute('data-title') + ' — added', openDrawer, add); return; }
    if (t.closest('[data-bench-all]')) { toggleAll(); return; }
    if (t.closest('[data-bench-clear]')) { bench = {}; saveBench(); scheduleRender(); return; }
    if (t.closest('[data-bench-add]')) { benchAddToCart(); return; }
    var th = t.closest('.afx-th[data-sort-asc]'); if (th) { e.preventDefault(); onSortClick(th); return; }
    var nav = t.closest('.afx-tab[href], .afx-page[href], .afx-clear[href]');
    if (nav) { e.preventDefault(); afxNavigate(nav.getAttribute('href'), true); return; }
  }
  function onRootChange(e) {
    var t = e.target;
    var cb = t.closest && t.closest('.afx-bench-cb');
    if (cb) { setSel(cb, cb.checked); saveBench(); scheduleRender(); return; }
    if (t.matches && t.matches('[data-facet-input], [data-sort-select]')) submitFacetsAjax(t);
  }
  function onKeydown(e) {
    onDrawerKeydown(e);
    // let Enter/Space on the select-all "checkbox" button behave like a toggle (it is a <button>, native)
  }
  function onPopState() { afxNavigate(location.href, false); }

  /* ====================================================================
     BOOT / TEARDOWN (idempotent; theme-editor lifecycle aware).
     ==================================================================== */
  function boot(scope) {
    var r = (scope && scope.matches && scope.matches('.afx-coll')) ? scope
      : (scope || document).querySelector ? (scope || document).querySelector('.afx-coll') : null;
    if (!r || r.__afxBooted) return;
    r.__afxBooted = true;
    root = r;

    var sectionEl = root.closest('.shopify-section, [id]') || root;
    BENCH_KEY = 'afx:bench:' + ((sectionEl && sectionEl.id) || 'default');
    bench = loadBench();
    toastEl = $('[data-toast]');

    root.addEventListener('click', onRootClick);
    root.addEventListener('change', onRootChange, { passive: true });
    document.addEventListener('keydown', onKeydown);
    window.addEventListener('popstate', onPopState);

    root.__afxTeardown = function () {
      clearTimeout(toastT);
      root.removeEventListener('click', onRootClick);
      root.removeEventListener('change', onRootChange);
      document.removeEventListener('keydown', onKeydown);
      window.removeEventListener('popstate', onPopState);
      if (ro) { try { ro.disconnect(); } catch (e) {} }
    };

    initView();
  }

  function bootAll() { boot(document); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bootAll);
  else bootAll();

  document.addEventListener('shopify:section:load', function (e) {
    var t = e.target;
    if (t && t.querySelector && t.querySelector('.afx-coll')) {
      // editor reloaded the section: drop the old root + re-boot the fresh one
      var fresh = t.querySelector('.afx-coll');
      if (root && root.__afxTeardown && root !== fresh) { root.__afxTeardown(); root.__afxBooted = false; }
      boot(t);
    }
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var t = e.target;
    var r = t && t.querySelector && t.querySelector('.afx-coll');
    if (r && r.__afxTeardown) { r.__afxTeardown(); r.__afxBooted = false; }
  });
})();
